/* SolmanMonthlyReport.jsx — tab "Monthly Report" pada dashboard Solman.
 *
 * Menyiapkan laporan bulanan Solman: file Excel yang mempertahankan struktur
 * workbook asli (pivot, slicer, chart, formula tetap hidup) plus draft email
 * siap paste ke Outlook, dan jadwal pengiriman otomatis.
 *
 * Seluruh pekerjaan berat ada di backend (solmanReport.js); komponen ini murni
 * pemanggil + preview.
 */

import { useState, useEffect, useCallback, useMemo } from "react";

/* ── Akses API ──────────────────────────────────────────────────────── */

const authToken = () =>
  localStorage.getItem("auth_token") ||
  new URLSearchParams(window.location.search).get("token") ||
  "";

async function api(url, opts = {}) {
  const res = await fetch(url, {
    ...opts,
    headers: {
      ...(opts.body instanceof FormData ? {} : { "Content-Type": "application/json" }),
      Authorization: `Bearer ${authToken()}`,
      ...(opts.headers || {}),
    },
  });
  const text = await res.text();
  let data = null;
  try { data = text ? JSON.parse(text) : null; } catch (_) { data = { raw: text }; }
  if (!res.ok) throw new Error((data && data.error) || `HTTP ${res.status}`);
  return data;
}

/* ── Bagian tampilan kecil ──────────────────────────────────────────── */

const CARD = {
  background: "var(--card-bg, #fff)", border: "1px solid var(--card-border, #e3e8ef)",
  borderRadius: 12, padding: 16, marginBottom: 16,
};
const BTN = {
  padding: "8px 14px", borderRadius: 8, border: "1px solid #1F4E79",
  background: "#1F4E79", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600,
};
const SECTION = {
  fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase",
  opacity: 0.55, margin: "0 0 8px 2px",
};
const BTN_GHOST = { ...BTN, background: "transparent", color: "#1F4E79" };

const MONTH_OPTS = [
  { v: "01", n: "Januari" },   { v: "02", n: "Februari" }, { v: "03", n: "Maret" },
  { v: "04", n: "April" },     { v: "05", n: "Mei" },      { v: "06", n: "Juni" },
  { v: "07", n: "Juli" },      { v: "08", n: "Agustus" },  { v: "09", n: "September" },
  { v: "10", n: "Oktober" },   { v: "11", n: "November" }, { v: "12", n: "Desember" },
];
const INPUT = {
  padding: "7px 10px", borderRadius: 8, border: "1px solid #cbd5e1",
  fontSize: 13, width: "100%", boxSizing: "border-box",
};

function Stat({ label, value, tone }) {
  return (
    <div style={{ flex: "1 1 130px", padding: "10px 14px", borderRadius: 10,
                  background: tone === "warn" ? "#FEF3E7" : "#F4F7FB",
                  border: `1px solid ${tone === "warn" ? "#F3C79B" : "#DCE6F2"}` }}>
      <div style={{ fontSize: 11, opacity: 0.7, textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color: tone === "warn" ? "#B4530A" : "#1F4E79" }}>{value}</div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label style={{ display: "block", marginBottom: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{label}</div>
      {children}
      {hint ? <div style={{ fontSize: 11, opacity: 0.65, marginTop: 3 }}>{hint}</div> : null}
    </label>
  );
}

/* ── Komponen utama ─────────────────────────────────────────────────── */

// `exportPanel` adalah panel Export Excel milik SolmanDashboard yang dioper apa
// adanya. Dioper sebagai elemen, bukan dipindah kodenya, supaya panel itu tetap
// punya satu sumber kebenaran dan tidak perlu ikut diubah saat halaman ini berubah.
export default function SolmanMonthlyReport({ exportPanel }) {
  const [cfg, setCfg]           = useState(null);
  // `draft` menampung ketikan yang belum disimpan. Dipisah dari `cfg` supaya
  // tombol Simpan tahu persis apa yang berubah, dan agar nilai yang dipakai
  // tombol Kirim tetap nilai yang sudah tersimpan di server.
  const [draft, setDraft]       = useState({});
  const [tpl, setTpl]           = useState(null);
  const [period, setPeriod]     = useState("");
  const [span, setSpan]         = useState(1);
  const [preview, setPreview]   = useState(null);
  const [cron, setCron]         = useState(null);
  const [cronSched, setCronSched] = useState("");
  const [busy, setBusy]         = useState("");
  const [msg, setMsg]           = useState(null);       // {type,text}

  const note = (type, text) => setMsg({ type, text });

  /* Muat konfigurasi + jadwal */
  const loadConfig = useCallback(async () => {
    try {
      const d = await api("/api/solman-report/config");
      setCfg(d.config); setDraft(d.config); setTpl(d.template);
      setPeriod((p) => p || d.defaultPeriod);
      setSpan(Number(d.config.months_span) || 1);
    } catch (e) { note("error", e.message); }
    try {
      const c = await api("/api/cron");
      const list = Array.isArray(c) ? c : (c.jobs || c.items || []);
      const job = list.find((j) => (j.key || j.cron_key) === "solman_monthly_report") || null;
      setCron(job);
      setCronSched(job ? (job.schedule || job.cron || "") : "");
    } catch (_) { /* jadwal opsional — jangan gagalkan halaman */ }
  }, []);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  /* Muat preview tiap periode / rentang berubah */
  useEffect(() => {
    if (!period) return;
    let alive = true;
    setBusy("preview");
    api(`/api/solman-report/preview?period=${encodeURIComponent(period)}&span=${span}`)
      .then((d) => { if (alive) setPreview(d); })
      .catch((e) => { if (alive) note("error", e.message); })
      .finally(() => { if (alive) setBusy(""); });
    return () => { alive = false; };
  }, [period, span]);

  const a = preview?.analysis;
  const mail = preview?.mail;

  /* ── Aksi ─────────────────────────────────────────────────────────── */

  async function download(chain) {
    setBusy(chain ? "chain" : "download");
    try {
      const res = await fetch(
        `/api/solman-report/xlsx?period=${encodeURIComponent(period)}&span=${span}${chain ? "&chain=1" : ""}`,
        { headers: { Authorization: `Bearer ${authToken()}` } });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `HTTP ${res.status}`);
      const stats = res.headers.get("X-Report-Stats");
      const blob = await res.blob();
      const cd = res.headers.get("Content-Disposition") || "";
      const name = (/filename="([^"]+)"/.exec(cd) || [])[1] || `Solman tickets analysis ${period}.xlsx`;
      const url = URL.createObjectURL(blob);
      const el = document.createElement("a");
      el.href = url; el.download = name; document.body.appendChild(el); el.click();
      el.remove(); URL.revokeObjectURL(url);
      const s = stats ? JSON.parse(stats) : null;
      note("ok", s
        ? `File dibuat: ${s.appended} tiket baru ditambahkan, ${s.updated} status diperbarui, sheet "${s.sheetName}".`
        : "File berhasil dibuat.");
      if (chain) loadConfig();
    } catch (e) { note("error", e.message); } finally { setBusy(""); }
  }

  async function sendNow() {
    if (!window.confirm(
      `Kirim email laporan ${a ? a.label : period} ke ${cfg?.email_to || "(belum diisi)"}?`)) return;
    setBusy("send");
    try {
      const r = await api("/api/solman-report/send", {
        method: "POST", body: JSON.stringify({ period, span }) });
      note("ok", `Email terkirim ke ${(r.recipients || []).join(", ")}.`);
      loadConfig();
    } catch (e) { note("error", e.message); } finally { setBusy(""); }
  }

  async function copyEmail() {
    if (!mail) return;
    try {
      // Salin sebagai text/html supaya tabelnya tetap tabel saat di-paste ke Outlook,
      // dengan text/plain sebagai cadangan untuk editor yang menolak HTML.
      if (navigator.clipboard && window.ClipboardItem) {
        await navigator.clipboard.write([new window.ClipboardItem({
          "text/html": new Blob([mail.html], { type: "text/html" }),
          "text/plain": new Blob([mail.text], { type: "text/plain" }),
        })]);
      } else {
        await navigator.clipboard.writeText(mail.html);
      }
      note("ok", "Body email tersalin — paste langsung ke Outlook.");
    } catch (e) { note("error", "Gagal menyalin: " + e.message); }
  }

  async function uploadTemplate(file) {
    if (!file) return;
    setBusy("upload");
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api("/api/solman-report/template", { method: "POST", body: fd });
      note("ok", "Template tersimpan. Bulan berikutnya akan memakai file ini sebagai dasar.");
      loadConfig();
    } catch (e) { note("error", e.message); } finally { setBusy(""); }
  }

  const EDITABLE = ["email_to", "email_cc", "subject_tpl", "sender_name",
                    "sp_ppi", "sp_pmi", "sp_pii", "sp_osi", "signature"];
  const setF = (k, v) => setDraft((p) => ({ ...p, [k]: v }));
  const dirty = !!cfg && EDITABLE.some((k) => (draft[k] || "") !== (cfg[k] || ""));

  async function saveCfg() {
    setBusy("save");
    try {
      const patch = {};
      EDITABLE.forEach((k) => { patch[k] = draft[k] || ""; });
      const d = await api("/api/solman-report/config", { method: "PUT", body: JSON.stringify(patch) });
      setCfg(d.config); setDraft(d.config); note("ok", "Pengaturan tersimpan.");
    } catch (e) { note("error", e.message); } finally { setBusy(""); }
  }

  async function saveCron(patch) {
    setBusy("cron");
    try {
      await api("/api/cron/solman_monthly_report", { method: "PUT", body: JSON.stringify(patch) });
      note("ok", "Jadwal tersimpan.");
      loadConfig();
    } catch (e) { note("error", e.message); } finally { setBusy(""); }
  }

  const yearOptions = useMemo(() => {
    const now = new Date().getUTCFullYear();
    return [now + 1, now, now - 1, now - 2, now - 3].map(String);
  }, []);

  if (!cfg) return <div style={{ padding: 20, opacity: 0.7 }}>Memuat…</div>;

  return (
    <div style={{ maxWidth: 1180 }}>
      {msg && (
        <div style={{ ...CARD, padding: "10px 14px", marginBottom: 12,
                      background: msg.type === "ok" ? "#E9F7EF" : "#FDECEA",
                      borderColor: msg.type === "ok" ? "#A9DFBF" : "#F5B7B1" }}>
          <span style={{ fontSize: 13 }}>{msg.text}</span>
          <button onClick={() => setMsg(null)}
                  style={{ float: "right", border: 0, background: "none", cursor: "pointer" }}>×</button>
        </div>
      )}

      {exportPanel && (
        <div style={{ marginBottom: 18 }}>
          <div style={SECTION}>Langkah 1 — Export per company &amp; upload SharePoint</div>
          {exportPanel}
        </div>
      )}

      <div style={SECTION}>Langkah 2 — Laporan konsolidasi &amp; email</div>

      {/* ── Periode + aksi ── */}
      <div style={CARD}>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div style={{ width: 140 }}>
            <Field label="Bulan laporan">
              <select style={INPUT} value={period.slice(5)}
                      onChange={(e) => setPeriod(`${period.slice(0, 4)}-${e.target.value}`)}>
                {MONTH_OPTS.map((m) => <option key={m.v} value={m.v}>{m.n}</option>)}
              </select>
            </Field>
          </div>
          <div style={{ width: 110 }}>
            <Field label="Tahun">
              <select style={INPUT} value={period.slice(0, 4)}
                      onChange={(e) => setPeriod(`${e.target.value}-${period.slice(5)}`)}>
                {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </Field>
          </div>
          <div style={{ width: 150 }}>
            <Field label="Rentang">
              <select style={INPUT} value={span} onChange={(e) => setSpan(Number(e.target.value))}>
                <option value={1}>1 bulan</option>
                <option value={2}>2 bulan</option>
                <option value={3}>3 bulan</option>
              </select>
            </Field>
          </div>
          <div style={{ flex: 1 }} />
          <button style={BTN_GHOST} disabled={!!busy} onClick={() => download(false)}>
            {busy === "download" ? "Menyiapkan…" : "Download Excel"}
          </button>
          <button style={BTN} disabled={!!busy || !tpl?.exists} onClick={() => download(true)}
                  title="Download sekaligus simpan sebagai template bulan berikutnya">
            {busy === "chain" ? "Memproses…" : "Generate & simpan template"}
          </button>
        </div>

        {!tpl?.exists && (
          <div style={{ marginTop: 10, fontSize: 12, color: "#B4530A" }}>
            Template belum ada. Upload file Excel laporan bulan sebelumnya di bagian Template di bawah.
          </div>
        )}

        {a && (
          <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
            <Stat label={`Tiket ${a.label}`} value={a.totals.periodTotal} />
            <Stat label="Closed" value={a.totals.periodClosed} />
            <Stat label="Masih open" value={a.totals.periodOpen} tone="warn" />
            <Stat label="Total unclosed" value={a.totals.openAll} tone="warn" />
            <Stat label="Unclosed lama" value={a.totals.olderOpen} tone="warn" />
          </div>
        )}
      </div>

      {/* ── Draft email ── */}
      <div style={CARD}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 10 }}>
          <b style={{ fontSize: 14 }}>Draft email</b>
          <div style={{ flex: 1 }} />
          <button style={BTN_GHOST} disabled={!mail} onClick={copyEmail}>Copy body</button>
          <button style={{ ...BTN, marginLeft: 8 }} disabled={!!busy || !tpl?.exists} onClick={sendNow}>
            {busy === "send" ? "Mengirim…" : "Kirim sekarang"}
          </button>
        </div>
        <div style={{ marginBottom: 10 }}>
          {/* To dan Cc bisa diubah langsung di sini — perubahan disimpan saat
              kursor meninggalkan kolom, jadi nilai yang terlihat selalu sama
              dengan yang dipakai tombol Kirim dan oleh cron. */}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 260px" }}>
              <Field label="To" hint="Pisahkan beberapa alamat dengan koma">
                <input style={{ ...INPUT, fontSize: 12 }} value={draft.email_to || ""}
                       placeholder="nama@perusahaan.com"
                       onChange={(e) => setF("email_to", e.target.value)} />
              </Field>
            </div>
            <div style={{ flex: "1 1 260px" }}>
              <Field label="Cc" hint="Boleh dikosongkan">
                <input style={{ ...INPUT, fontSize: 12 }} value={draft.email_cc || ""}
                       placeholder="(kosong)"
                       onChange={(e) => setF("email_cc", e.target.value)} />
              </Field>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ fontSize: 12, flex: 1 }}><b>Subject:</b> {mail ? mail.subject : "…"}</div>
            {dirty && <span style={{ fontSize: 11, color: "#B4530A" }}>Ada perubahan belum disimpan</span>}
            <button style={{ ...BTN_GHOST, opacity: dirty ? 1 : 0.45 }}
                    disabled={!dirty || !!busy} onClick={saveCfg}>
              {busy === "save" ? "Menyimpan…" : "Simpan pengaturan"}
            </button>
          </div>
        </div>
        <div style={{ border: "1px solid #e3e8ef", borderRadius: 8, padding: 12,
                      maxHeight: 460, overflow: "auto", background: "#fff" }}>
          {busy === "preview"
            ? <div style={{ opacity: 0.6 }}>Menyusun preview…</div>
            : <div dangerouslySetInnerHTML={{ __html: mail ? mail.html : "" }} />}
        </div>
      </div>

      {/* ── Template ── */}
      <div style={CARD}>
        <b style={{ fontSize: 14 }}>Template Excel</b>
        <p style={{ fontSize: 12, opacity: 0.75, marginTop: 6 }}>
          File hasil generate disimpan otomatis sebagai template bulan berikutnya, jadi pivot,
          slicer, chart, dan formula di dalamnya terus terbawa tanpa perlu upload ulang.
        </p>
        <div style={{ fontSize: 12, marginBottom: 10 }}>
          {tpl?.exists
            ? <>Template aktif · {(tpl.size / 1024 / 1024).toFixed(2)} MB · diperbarui{" "}
                {new Date(tpl.updated_at).toLocaleString("id-ID")}</>
            : <span style={{ color: "#B4530A" }}>Belum ada template.</span>}
        </div>
        <input type="file" accept=".xlsx" disabled={!!busy}
               onChange={(e) => uploadTemplate(e.target.files && e.target.files[0])} />
      </div>

      {/* ── Jadwal ── */}
      <div style={CARD}>
        <b style={{ fontSize: 14 }}>Jadwal pengiriman otomatis</b>
        {cron ? (
          <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap", marginTop: 10 }}>
            <div style={{ width: 190 }}>
              <Field label="Cron (menit jam hari bulan hari-pekan)" hint="Contoh: 0 8 3 * * — tiap tanggal 3 pukul 08:00">
                <input style={INPUT} value={cronSched}
                       onChange={(e) => setCronSched(e.target.value)} />
              </Field>
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 14, fontSize: 13 }}>
              <input type="checkbox" defaultChecked={!!cron.enabled}
                     onChange={(e) => saveCron({ enabled: e.target.checked })} />
              Aktif
            </label>
            <button style={{ ...BTN_GHOST, marginBottom: 14 }} disabled={!!busy}
                    onClick={() => saveCron({ schedule: cronSched })}>
              {busy === "cron" ? "Menyimpan…" : "Simpan jadwal"}
            </button>
            <div style={{ flex: 1 }} />
            <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 14 }}>
              Terakhir kirim: {cfg.last_period || "-"} {cfg.last_status ? `· ${cfg.last_status}` : ""}
            </div>
          </div>
        ) : (
          <p style={{ fontSize: 12, opacity: 0.75, marginTop: 8 }}>
            Jadwal <code>solman_monthly_report</code> belum terdaftar di scheduler — atur lewat halaman
            System / Cron setelah backend di-restart.
          </p>
        )}
      </div>

      {/* ── Pengaturan ── */}
      <details style={CARD}>
        <summary style={{ cursor: "pointer", fontWeight: 700, fontSize: 14 }}>Pengaturan email &amp; link</summary>
        <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 18px" }}>
          
          <Field label="Subject" hint="{PERIOD} diganti otomatis, mis. Jul-Aug 2026">
            <input style={INPUT} value={draft.subject_tpl || ""} onChange={(e) => setF("subject_tpl", e.target.value)} />
          </Field>
          <Field label="Nama pengirim">
            <input style={INPUT} value={draft.sender_name || ""} onChange={(e) => setF("sender_name", e.target.value)} />
          </Field>
          {["ppi", "pmi", "pii", "osi"].map((k) => (
            <Field key={k} label={`Link SharePoint ${k.toUpperCase()}`}>
              <input style={INPUT} value={draft[`sp_${k}`] || ""}
                     onChange={(e) => setF(`sp_${k}`, e.target.value)} />
            </Field>
          ))}
          <Field label="Tanda tangan">
            <textarea style={{ ...INPUT, minHeight: 60 }} value={draft.signature || ""}
                      onChange={(e) => setF("signature", e.target.value)} />
          </Field>
        </div>
        <div style={{ marginTop: 4 }}>
          {dirty && <span style={{ fontSize: 11, color: "#B4530A", marginRight: 10 }}>Ada perubahan belum disimpan</span>}
          <button style={{ ...BTN, opacity: dirty ? 1 : 0.45 }} disabled={!dirty || !!busy} onClick={saveCfg}>
            {busy === "save" ? "Menyimpan…" : "Simpan pengaturan"}
          </button>
        </div>
      </details>
    </div>
  );
}
